import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "../lib/utils";
import { motion } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";

const timelineEvents = [
  { id: 1, date: "2024-01-15", title: "Project Alpha Launch", type: "milestone", entities: ["TechCorp", "John Smith"], severity: "high" },
  { id: 2, date: "2024-01-22", title: "Partnership Announced", type: "event", entities: ["DataFlow", "TechCorp"], severity: "medium" },
  { id: 3, date: "2024-02-01", title: "Q1 Report Published", type: "document", entities: ["TechCorp"], severity: "low" },
  { id: 4, date: "2024-02-15", title: "Security Incident", type: "alert", entities: ["Network Hub"], severity: "critical" },
  { id: 5, date: "2024-02-28", title: "New Investment Round", type: "transaction", entities: ["Capital V", "TechCorp"], severity: "high" },
  { id: 6, date: "2024-03-10", title: "Merger Completed", type: "milestone", entities: ["TechCorp", "DataFlow"], severity: "high" },
];

const activityData = [
  { date: "Jan", events: 45 }, { date: "Feb", events: 62 }, { date: "Mar", events: 58 },
  { date: "Apr", events: 78 }, { date: "May", events: 85 }, { date: "Jun", events: 92 },
];

const typeConfig = {
  milestone: { icon: "fa-flag", color: "hsl(var(--chart-1))", label: "Milestone" },
  event: { icon: "fa-calendar-check", color: "hsl(var(--chart-2))", label: "Event" },
  document: { icon: "fa-file-lines", color: "hsl(var(--chart-3))", label: "Document" },
  alert: { icon: "fa-triangle-exclamation", color: "hsl(var(--destructive))", label: "Alert" },
  transaction: { icon: "fa-money-bill-transfer", color: "hsl(var(--chart-4))", label: "Transaction" },
};

const severityConfig = {
  critical: { color: "bg-destructive", text: "text-destructive" },
  high: { color: "bg-warning", text: "text-warning" },
  medium: { color: "bg-primary", text: "text-primary" },
  low: { color: "bg-muted-foreground", text: "text-muted-foreground" },
};

export default function TimelineAnalytics() {
  const { t } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState("6m");
  const [filterTypes, setFilterTypes] = useState(Object.keys(typeConfig));

  const toggleFilter = (type) => setFilterTypes(filterTypes.includes(type) ? filterTypes.filter(t => t !== type) : [...filterTypes, type]);
  const filteredEvents = timelineEvents.filter(event => filterTypes.includes(event.type));

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden p-2 border-b border-border bg-card/50 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}><i className="fa-solid fa-filter mr-2"></i>{t("filters")}</Button>
        <Select value={dateRange} onValueChange={setDateRange}><SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1m">{t("lastMonth")}</SelectItem><SelectItem value="3m">3 {t("months")}</SelectItem><SelectItem value="6m">6 {t("months")}</SelectItem></SelectContent></Select>
      </div>

      {/* Left Panel - Filters */}
      <div className={cn("w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-border bg-card/50 flex flex-col", showFilters ? "block" : "hidden lg:flex", "max-h-[35vh] lg:max-h-none")}>
        <div className="p-3 lg:p-4 border-b border-border hidden lg:block"><h3 className="font-semibold text-foreground flex items-center gap-2 text-sm"><i className="fa-solid fa-filter text-primary"></i>{t("eventFilters")}</h3></div>
        <ScrollArea className="flex-1 p-3 lg:p-4">
          <div className="space-y-4">
            <div className="hidden lg:block space-y-3">
              <Label className="text-xs sm:text-sm font-medium">{t("dateRange")}</Label>
              <Select value={dateRange} onValueChange={setDateRange}><SelectTrigger className="h-8 sm:h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1m">{t("lastMonth")}</SelectItem><SelectItem value="3m">3 {t("months")}</SelectItem><SelectItem value="6m">6 {t("months")}</SelectItem></SelectContent></Select>
            </div>
            <Separator className="hidden lg:block" />
            <div className="space-y-2">
              <Label className="text-xs sm:text-sm font-medium">{t("categories")}</Label>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {Object.entries(typeConfig).map(([type, config]) => (
                  <div key={type} onClick={() => toggleFilter(type)} className={cn("flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors", filterTypes.includes(type) ? "bg-accent" : "opacity-50 hover:opacity-75")}>
                    <div className="flex h-6 w-6 lg:h-8 lg:w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${config.color}20` }}>
                      <i className={cn("fa-solid", config.icon, "text-xs lg:text-sm")} style={{ color: config.color }}></i>
                    </div>
                    <span className="text-xs lg:text-sm">{config.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Main Timeline Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Charts - Hidden on mobile */}
        <div className="hidden sm:block p-3 lg:p-6 border-b border-border">
          <Card>
            <CardHeader className="pb-2 p-3 lg:p-6"><CardTitle className="text-sm lg:text-base">{t("timelineView")}</CardTitle></CardHeader>
            <CardContent className="p-2 lg:p-6">
              <div className="h-32 lg:h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityData}>
                    <defs><linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3}/><stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", fontSize: "12px" }}/>
                    <Area type="monotone" dataKey="events" stroke="hsl(var(--chart-1))" fillOpacity={1} fill="url(#colorEvents)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <ScrollArea className="flex-1 p-3 lg:p-6">
          <div className="space-y-1">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h3 className="text-base lg:text-lg font-semibold">{t("timelineExplorer")}</h3>
              <span className="text-xs lg:text-sm text-muted-foreground">{filteredEvents.length} {t("events")}</span>
            </div>

            <div className="relative">
              <div className="absolute left-4 lg:left-8 top-0 bottom-0 w-0.5 bg-border"></div>
              <div className="space-y-3 lg:space-y-4">
                {filteredEvents.map((event, index) => {
                  const config = typeConfig[event.type];
                  const severity = severityConfig[event.severity];
                  const isSelected = selectedEvent?.id === event.id;

                  return (
                    <motion.div key={event.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }} onClick={() => setSelectedEvent(isSelected ? null : event)} className="relative flex items-start gap-3 lg:gap-4 pl-2 lg:pl-4 cursor-pointer group">
                      <div className={cn("relative z-10 flex h-6 w-6 lg:h-8 lg:w-8 shrink-0 items-center justify-center rounded-full border-2 lg:border-4 transition-all", isSelected ? "border-primary scale-110" : "border-background group-hover:border-primary/50")} style={{ backgroundColor: config.color }}>
                        <i className={cn("fa-solid", config.icon, "text-[10px] lg:text-xs text-white")}></i>
                      </div>
                      <Card className={cn("flex-1 transition-all", isSelected ? "border-primary shadow-glow" : "hover:border-primary/50")}>
                        <CardContent className="p-3 lg:p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-foreground text-xs lg:text-sm truncate">{event.title}</h4>
                                <Badge variant="outline" className={cn("text-[10px] lg:text-xs capitalize", severity.text)}>{t(event.severity) || event.severity}</Badge>
                              </div>
                              <p className="text-[10px] lg:text-sm text-muted-foreground">{event.date}</p>
                            </div>
                            <Badge variant="secondary" className="text-[10px] lg:text-xs shrink-0">{config.label}</Badge>
                          </div>
                          <div className="mt-2 lg:mt-3 flex items-center gap-1 lg:gap-2 flex-wrap">
                            {event.entities.slice(0, 2).map((entity, idx) => (<Badge key={idx} variant="outline" className="text-[10px] lg:text-xs"><i className="fa-solid fa-link mr-1 text-[8px] lg:text-[10px]"></i>{entity}</Badge>))}
                            {event.entities.length > 2 && <Badge variant="outline" className="text-[10px] lg:text-xs">+{event.entities.length - 2}</Badge>}
                          </div>
                          {isSelected && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-border">
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline" className="h-7 lg:h-8 text-[10px] lg:text-xs"><i className="fa-solid fa-diagram-project mr-1 lg:mr-2"></i>{t("graph")}</Button>
                                <Button size="sm" className="h-7 lg:h-8 text-[10px] lg:text-xs"><i className="fa-solid fa-magnifying-glass mr-1 lg:mr-2"></i>{t("investigate")}</Button>
                              </div>
                            </motion.div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Metrics (Hidden on mobile) */}
      <div className="hidden xl:flex w-64 border-l border-border bg-card/50 flex-col">
        <div className="p-4 border-b border-border"><h3 className="font-semibold text-foreground flex items-center gap-2 text-sm"><i className="fa-solid fa-chart-simple text-primary"></i>{t("metrics")}</h3></div>
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-primary">{timelineEvents.length}</p><p className="text-xs text-muted-foreground">{t("events")}</p></CardContent></Card>
              <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-destructive">{timelineEvents.filter(e => e.severity === "critical").length}</p><p className="text-xs text-muted-foreground">{t("critical")}</p></CardContent></Card>
            </div>
            <Separator />
            <div className="space-y-3">
              <h4 className="text-sm font-medium">{t("type")}</h4>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.entries(typeConfig).map(([type, config]) => ({ name: config.label.slice(0, 4), count: timelineEvents.filter(e => e.type === type).length, fill: config.color }))} layout="vertical">
                    <XAxis type="number" hide /><YAxis dataKey="name" type="category" width={40} fontSize={10} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", fontSize: "12px" }}/>
                    <Bar dataKey="count" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
